import { useState } from "react";
import Icon1 from "@assets/icons/Icon";
import BaseModal from "@components/modals/BaseModal";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import Button from "@components/ui/buttons/Button";
import TextField from "@components/ui/textfields/TextField";
import DropDown from "@components/ui/dropdown/DropDown";
import DatePicker from "@components/ui/selection/DatePicker";
import VehicleImage from "@components/ui/VehicleImage";
import VehiclePartiesEditor, { isBlockEmpty } from "@components/vehicles/VehiclePartiesEditor";
import type { ClientVehicle, VehiclePartyBlock, ClientVehicleColoris } from "types/client_vehicle";
import { isMecFullDate, mecPickerLabel } from "@utils/vehicleMec";

const COLORIS_OPTIONS = [
  { value: "noir", label: "Noir" },
  { value: "blanc", label: "Blanc" },
  { value: "gris", label: "Gris" },
  { value: "bleu", label: "Bleu" },
  { value: "rouge", label: "Rouge" },
  { value: "vert", label: "Vert" },
  { value: "autre", label: "Autre" },
];

export interface ClientVehicleReviewModalProps {
  vehicle: ClientVehicle;
  onSave: (data: Partial<ClientVehicle>) => void;
  onClose: () => void;
  saving?: boolean;
}

export default function ClientVehicleReviewModal({ vehicle, onSave, onClose, saving }: ClientVehicleReviewModalProps) {
  const [plate, setPlate] = useState(vehicle.plate || "");
  const [marque, setMarque] = useState(vehicle.marque || "");
  const [modele, setModele] = useState(vehicle.modele || "");
  const [coloris, setColoris] = useState(vehicle.coloris || "");
  const [km, setKm] = useState(vehicle.km != null ? String(vehicle.km) : "");
  const [vin, setVin] = useState(vehicle.vin || "");
  const [mec, setMec] = useState<number | null>(vehicle.mec || null);
  const [isFleet, setIsFleet] = useState<boolean>(!!vehicle.is_fleet);
  const [owner, setOwner] = useState<VehiclePartyBlock | null>(vehicle.owner || null);
  const [company, setCompany] = useState<VehiclePartyBlock | null>(vehicle.company || null);
  const [user, setUser] = useState<VehiclePartyBlock | null>(vehicle.user || null);

  const handleSave = () => {
    onSave({
      plate,
      marque,
      modele,
      coloris: (coloris || undefined) as ClientVehicleColoris | undefined,
      km: km === "" ? undefined : Number(km),
      vin,
      mec: mec || undefined,
      is_fleet: isFleet,
      owner: isBlockEmpty(owner) ? null : owner,
      company: isBlockEmpty(company) ? null : company,
      user: isBlockEmpty(user) ? null : user,
    });
  };

  return (
    <BaseModal onClose={onClose} title={plate || "Véhicule"} width="max-w-2xl" startIcon={<Icon1 name="car" size={20} />}>
      <div className="flex flex-col gap-4 p-4 max-h-[75vh] overflow-y-auto">
        {/* Vehicle core */}
        <div className="w-full rounded-xl border border-neutral-200 bg-neutral-50 flex items-center justify-center overflow-hidden" style={{ height: 140 }}>
          <VehicleImage marque={marque} modele={modele} coloris={coloris} mec={mec || undefined} alt={plate} className="w-full h-full" />
        </div>
        <div className="flex flex-row gap-2">
          <div className="flex-1">
            <TextField label="Immatriculation" type="plate" value={plate} onChange={setPlate} />
          </div>
          <div className="flex-1">
            <DropDown label="Coloris" options={COLORIS_OPTIONS} value={coloris} onSelect={(v) => setColoris(String(v))} />
          </div>
        </div>
        <div className="flex flex-row gap-2">
          <div className="flex-1">
            <TextField label="Marque" value={marque} onChange={setMarque} />
          </div>
          <div className="flex-1">
            <TextField label="Modèle" value={modele} onChange={setModele} />
          </div>
        </div>
        <div className="flex flex-row gap-2">
          <div className="flex-1">
            <TextField label="Km" type="number" value={km} onChange={setKm} />
          </div>
          <div className="flex-1">
            <DatePicker label={mecPickerLabel(mec)} seconds={isMecFullDate(mec) ? mec : null} onChange={setMec} />
          </div>
        </div>
        <TextField label="VIN" value={vin} onChange={setVin} />

        {/* is_fleet + party blocks (shared with the dossier-detail vehicle modal) */}
        <VehiclePartiesEditor
          value={{ is_fleet: isFleet, owner, company, user }}
          onChange={(v) => {
            setIsFleet(v.is_fleet);
            setOwner(v.owner);
            setCompany(v.company);
            setUser(v.user);
          }}
        />

        <div className="flex flex-row justify-end gap-2 pt-1">
          <SecondaryButton variant="small" onClick={onClose}>Annuler</SecondaryButton>
          <Button variant="small" label={saving ? "Enregistrement..." : "Enregistrer"} onClick={handleSave} disabled={saving} />
        </div>
      </div>
    </BaseModal>
  );
}
